import React, { useEffect, useState } from 'react';
import { LegacyPreviewEmail, PreviewEmail, PreviewItem, StepCard, previewWorkflowSteps } from '../../../api/cafAutomation';
import { CatalogueReviews } from '../../../api/emailCatalogue';
import { isProviderEmail, proofMatches, reviewProof } from '../../email-catalogue/reviewProofs';
import EmailSettingsReview from '../../email-catalogue/EmailSettingsReview';
import { Modal } from '../Overlay';
import { EMAIL_NAMES, forwardReviewKey, isConfiguredForward, recipientLabel, stepTitle } from './steps';

const FAILED = "Couldn't show the preview. Close and try again.";

const OneEmail: React.FC<{ email: PreviewEmail; frameTitle: string }> = ({ email, frameTitle }) => isProviderEmail(email) ? <section>
  <p>{email.conditional_explanation}</p>
  <EmailSettingsReview review={email.settings} title={email.purpose} />
</section> : <LegacyEmail email={email} frameTitle={frameTitle} />;

const LegacyEmail: React.FC<{ email: LegacyPreviewEmail; frameTitle: string }> = ({ email, frameTitle }) => (
  <section className="cur-stack">
    {email.recipient === 'lender' && <div><span className="cur-pill cur-pill--amber">{recipientLabel('lender')}</span></div>}
    <div><span className="cur-muted">Subject: </span><strong>{email.subject}</strong></div>
    {email.made_up.length > 0 && (
      <div className="cur-made-up" aria-label="Made-up details">
        <span className="cur-hint">Made-up details:</span>
        {email.made_up.map((value) => <span key={value}>{value}</span>)}
      </div>
    )}
    {/* The HTML is our template with made-up values. sandbox="" allows no scripts, forms or popups, and the base target makes link clicks do nothing. */}
    <iframe className="cur-email-frame" title={frameTitle} sandbox="" srcDoc={`<base target="_blank">${email.html}`} />
  </section>
);

/** Every template a ready item sends (the email and, for multi-rep with reminders on, its reminder). */
const ticksOf = (item: PreviewItem): Record<string, string> => {
  if (item.status !== 'ready') return {};
  const ticks: Record<string, string> = {};
  if (!isProviderEmail(item)) {
    ticks[item.template_id] = item.source_hash;
    if (item.review_key && item.review_hash) ticks[item.review_key] = item.review_hash;
  }
  if (item.reminder && !isProviderEmail(item.reminder)) ticks[item.reminder.template_id] = item.reminder.source_hash;
  return ticks;
};

const providerTicksOf = (item: PreviewItem): CatalogueReviews => {
  if (item.status !== 'ready') return {};
  return Object.fromEntries([item, item.reminder].filter(isProviderEmail).flatMap(email => {
    const proof = reviewProof(email as import('../../../api/emailCatalogue').ProviderEmailPreview);
    return proof ? [[(email as import('../../../api/emailCatalogue').ProviderEmailPreview).occurrence, proof]] : [];
  }));
};
const ready = (item: PreviewItem) => item.status === 'ready' && (!item.reminder || !isProviderEmail(item.reminder) || item.reminder.status === 'ready');

type Prior = 'none' | 'same' | 'changed';
const priorOf = (item: PreviewItem, reviewed: Record<string, string> | undefined, checked: boolean, catalogue: CatalogueReviews = {}): Prior => {
  if (item.status !== 'ready') return 'none';
  if (!reviewed) return checked ? 'same' : 'none';
  const ticks = ticksOf(item);
  const ids = Object.keys(ticks);
  const provider = [item, item.reminder].filter(isProviderEmail) as import('../../../api/emailCatalogue').ProviderEmailPreview[];
  if (ids.every((id) => reviewed[id] === ticks[id]) && provider.every(email => proofMatches(email, catalogue[email.occurrence]))) return 'same';
  return ids.some((id) => Boolean(reviewed[id])) || provider.some(email => !!catalogue[email.occurrence]) ? 'changed' : 'none';
};

/**
 * Shows what the card at `index` would send, rendered from our templates with made-up details.
 * A Cancel the claim card has one item per notice template, and each is ticked on its own.
 */
export const EmailPreviewModal: React.FC<{
  cards: StepCard[]; index: number; checked: boolean; onClose: () => void; onChecked: (ticks: Record<string, string>) => void;
  /** {template_id: hash} of what is already ticked; an item counts as checked only if the hash still matches. */
  reviewed?: Record<string, string>; reviewedCatalogue?: CatalogueReviews; onCatalogueChecked?: (proofs: CatalogueReviews) => void; readOnly?: boolean;
}> = ({ cards, index, checked, onClose, onChecked, reviewed, reviewedCatalogue = {}, onCatalogueChecked, readOnly = false }) => {
  const [items, setItems] = useState<PreviewItem[] | null>(null);
  const [problem, setProblem] = useState('');
  const [ticked, setTicked] = useState<number[]>([]);

  useEffect(() => {
    let alive = true;
    setItems(null); setTicked([]); setProblem('');
    previewWorkflowSteps(cards).then(
      (data) => {
        if (!alive) return;
        const mine = data.items.filter((candidate) => candidate.index === index);
        if (!mine.length) { setProblem(FAILED); return; }
        const card = cards[index];
        if (isConfiguredForward(card) && !isProviderEmail(card.covering_email) && mine.some((item) => item.status === 'ready'
          && !isProviderEmail(item) && (item.review_key !== forwardReviewKey(index) || !item.review_hash))) {
          setProblem('This covering email could not be verified. Close and preview it again.');
          return;
        }
        setItems(mine);
      },
      () => { if (alive) setProblem(FAILED); },
    );
    return () => { alive = false; };
  }, [cards, index]);

  const card = cards[index];
  const name = card && card.kind === 'email' ? EMAIL_NAMES[card.email] : card ? stepTitle(card) : 'Email';
  const readyPositions = (items || []).flatMap((item, position) => (ready(item) ? [position] : []));
  const many = (items || []).length > 1;

  const tick = (position: number) => {
    if (!items || readOnly || !ready(items[position])) return;
    onChecked(ticksOf(items[position]));
    onCatalogueChecked?.(providerTicksOf(items[position]));
    const now = ticked.indexOf(position) === -1 ? [...ticked, position] : ticked;
    setTicked(now);
    if (readyPositions.every((ready) => now.indexOf(ready) !== -1 || priorOf(items[ready], reviewed, checked, reviewedCatalogue) === 'same')) onClose();
  };

  return (
    <Modal title={`Preview: ${name}`} size="wide" onClose={onClose}
      footer={(
        <>
          <button type="button" className="ui-btn ui-btn--ghost ui-btn--md" onClick={onClose}>Close</button>
          {!readOnly && !many && items && ready(items[0]) && (
            <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={() => tick(0)}>
              {priorOf(items[0], reviewed, checked, reviewedCatalogue) === 'same' ? 'Checked again' : "I've checked this email"}
            </button>
          )}
        </>
      )}>
      {problem && <p className="cur-hint">{problem}</p>}
      {!items && !problem && <p className="cur-hint">Loading…</p>}
      {items && items.some((item) => item.status === 'ready') && <p className="cur-hint">Links and buttons don&apos;t work in previews.</p>}
      {items && items.map((item, position) => {
        const done = ticked.indexOf(position) !== -1;
        const prior = priorOf(item, reviewed, checked, reviewedCatalogue);
        return (
          <div key={`${item.email}-${isProviderEmail(item) ? item.occurrence : item.template_id || position}`} className="cur-stack">
            {many && !isProviderEmail(item) && <h3 className="cur-sec-title">{`Email ${position + 1} of ${items.length}`}</h3>}
            {item.status === 'not_set_up' && !isProviderEmail(item) && (
              <>
                {item.recipient === 'lender' && <div><span className="cur-pill cur-pill--amber">{recipientLabel('lender')}</span></div>}
                <p>This email isn&apos;t set up yet, so it can&apos;t be used.</p>
              </>
            )}
            {(item.status === 'ready' || isProviderEmail(item)) && (
              <>
                {item.email === 'forward' && (
                  <p className="cur-hint">{cards[index].kind === 'forward' && (cards[index] as Extract<StepCard, { kind: 'forward' }>).forwarding_scope === 'closed_claim_default' ? 'For cancelled claims only. ' : ''}This reviews the configured covering email. When sent, it includes the received lender email as a PDF and all its original attachments. Attachments are not shown in this preview.</p>
                )}
                <OneEmail email={item} frameTitle={many ? `Email preview ${position + 1}` : 'Email preview'} />
                {item.reminder && (
                  <>
                    <h3 className="cur-sec-title">The reminder, sent 7 days later if the client doesn&apos;t reply</h3>
                    <OneEmail email={item.reminder} frameTitle="Reminder preview" />
                  </>
                )}
                {prior === 'changed' && !done && <p className="cur-problem">This email changed since it was checked. Check it again.</p>}
                {!readOnly && many && ready(item) && (
                  <div>
                    <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={() => tick(position)}>
                      {prior === 'same' || done ? 'Checked again' : "I've checked this email"}
                    </button>
                    {(prior === 'same' || done) && <span className="cur-pill cur-pill--teal">Checked</span>}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </Modal>
  );
};
